import {
  Component,
  ErrorInfo,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { UnexpectedError } from './components/UnexpectedError';

interface Props extends PropsWithChildren {
  isCentered?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ error, errorInfo });
    // Optional: send to logging service here
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <UnexpectedError
          isCentered={this.props.isCentered}
          onRetry={this.handleRetry}
          error={this.state.error}
        />
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundaryWrapper = (
  props: React.ComponentProps<typeof ErrorBoundary>,
) => {
  const location = useLocation();
  const [key, setKey] = useState(0);

  useEffect(() => {
    setKey(prev => prev + 1);
  }, [location.pathname]);

  return <ErrorBoundary key={key} {...props} />;
};
